import type { BadgeTone } from '@/components/admin/entretiens/status-badge';
import { TRUNCATED_OUTPUT_SENTENCE } from '@/lib/conversation-model';
import type {
  AnalysisFailure,
  AnalysisLevel,
  AnalysisShares,
  AnalystFunction,
  AnalystLevel,
  AnalystPass,
  NoyauAnalysis,
  NoyauAnalystCall,
  NoyauAnalystPrompt,
  NoyauCode,
  NoyauDetermination,
  NoyauTrial,
  PlayedProtocol,
} from '@matter_extractor/shared';
import {
  ANALYSIS_CONFLICT_DECIDER_LABELS,
  ANALYSIS_LEVEL_STATE_LABELS,
  ANALYST_FUNCTIONS,
  ANALYST_FUNCTION_NAMES,
  ANALYST_LEVELS,
  ANALYST_LEVEL_LABELS,
  NOYAU_CODES,
  analystLevelsPhrase,
  eliminatedNoyaux,
  eliminatedSentence,
  isLevelFixed,
  noyauAnalystOutputSchema,
  noyauOf,
  sortedNoyauScores,
  unfenced,
} from '@matter_extractor/shared';

/**
 * What the parcours SAYS of the analyst in two passes (analyste.md §11.1, issue
 * #196, lot D; maquettes `ParcoursAvantApres` and `ParcoursEtats`) - the words and
 * the arrangements of the block, pure, in one module beside `session-noyau.ts`,
 * which says the rest of the screen. Every line below reads the `analysis` a
 * determination stores (`noyauAnalysisSchema`): the screen derives no level, no
 * candidate and no note, it only words what the code already decided.
 *
 * ## Two kinds of determination, and one trap between them
 *
 * A determination written by the analyst of BEFORE #196 has no `analysis` and calls
 * that carry no pass: the block draws it as it did, under the grey pill
 * « lecture directe, avant #196 » (`isDirectReading`). A determination of the new
 * analyst ALWAYS has an `analysis`, even `pending`, `no-extracts` or `failed` - the
 * header alone then - so « no analysis » is not « before #196 » for those. The
 * pill belongs to what has calls and no pass; a failure with no call at all says
 * its reason from `analysis.failure` (`failureSentence`).
 *
 * ## What a screen of this lot never uses: `analystReadingFacts`
 *
 * The line of the instrument of a determination in two passes is built HERE
 * (`instrumentFacts`) from the protocol the session froze and the analysis. The
 * reading of before (`analystReading`) is empty on every protocol born since the
 * section exists, so the helper that reads it would call every new determination
 * « générique » while the reading served was the section's own.
 */

/* ----------------------------------------------------------------- the kind */

/**
 * A determination (or a trial) the analyst of BEFORE #196 wrote: no analysis, and
 * calls that carry no pass. One that has no call at all - an old `no-extracts`, an
 * old failure before any call - read nothing, so it says nothing of a reading.
 */
export function isDirectReading(
  determination: Pick<NoyauDetermination, 'analysis' | 'calls'>,
): boolean {
  return (
    determination.analysis === null &&
    determination.calls.length > 0 &&
    determination.calls.every((call) => call.pass === null)
  );
}

/**
 * Does this determination have a REASONING to draw - the sections of §11.1? Only a
 * `rendered` one whose analysis went past the first pass: a failure, a pending
 * run and an empty matter have a header and nothing to show under it.
 */
export function hasReasoning(
  determination: Pick<NoyauDetermination, 'state' | 'analysis'>,
): boolean {
  const { analysis } = determination;
  return determination.state === 'rendered' && analysis !== null && analysis.levels.length > 0;
}

/** The pill of a determination whose second pass failed twice (§7.5). */
export function isStyleAbsent(analysis: NoyauAnalysis | null): boolean {
  return analysis?.style?.state === 'absent';
}

/** One piece of a sentence: `strong` is what the screen sets in bold, the figures and the names. */
export type TextPart = { readonly text: string; readonly strong: boolean };

const plain = (text: string): TextPart => ({ text, strong: false });
const bold = (text: string): TextPart => ({ text, strong: true });

/** « A », « A et B », « A, B et C » - each item a run of parts. */
function joinedWithAnd(items: readonly (readonly TextPart[])[]): TextPart[] {
  const parts: TextPart[] = [];
  items.forEach((item, index) => {
    if (index > 0) parts.push(plain(index === items.length - 1 ? ' et ' : ', '));
    parts.push(...item);
  });
  return parts;
}

/** « 72 et 84 », « 70, 72 et 80 », « 88 ». */
function sharesPhrase(shares: readonly number[]): string {
  return joinedWithAnd(shares.map((share) => [plain(String(share))]))
    .map((part) => part.text)
    .join('');
}

/** « 50 % », with the no-break space the planches write. */
const percent = (value: number): string => `${value} %`;

/* -------------------------------------------------------------- the calls */

/** « appel », « appels » - the word beside the count in bold. */
export function appelsWord(count: number): string {
  return count >= 2 ? 'appels' : 'appel';
}

/**
 * The passes the calls were made for, by order of appearance, with how many calls
 * each took: a pass is called once and retried ONCE whatever the failure (§5.3).
 */
export function passesOfCalls(
  calls: readonly Pick<NoyauAnalystCall, 'pass'>[],
): { pass: AnalystPass; calls: number }[] {
  const counts = new Map<AnalystPass, number>();
  for (const call of calls) {
    if (call.pass === null) continue;
    counts.set(call.pass, (counts.get(call.pass) ?? 0) + 1);
  }
  return [...counts].map(([pass, count]) => ({ pass, calls: count }));
}

/**
 * « passe 1, passe 2 relancée » - what the facts line says in brackets after the
 * count: a pass that took two calls was retried (the maquette `ParcoursEtats`,
 * « 3 appels (passe 1, passe 2 relancée) »).
 */
export function callsByPass(calls: readonly Pick<NoyauAnalystCall, 'pass'>[]): string {
  return passesOfCalls(calls)
    .map((entry) => (entry.calls > 1 ? `passe ${entry.pass} relancée` : `passe ${entry.pass}`))
    .join(', ');
}

/** One call with the pass it belongs to and its rank inside that pass: « passe 2, essai 2 ». */
export type RankedAttempt = {
  readonly call: NoyauAnalystCall;
  readonly pass: AnalystPass | null;
  readonly rank: number;
};

export function rankedAttempts(calls: readonly NoyauAnalystCall[]): RankedAttempt[] {
  const seen = new Map<AnalystPass | null, number>();
  return calls.map((call) => {
    const rank = (seen.get(call.pass) ?? 0) + 1;
    seen.set(call.pass, rank);
    return { call, pass: call.pass, rank };
  });
}

/** « Passe 1 · essai 2 » - or « Essai 2 » on a call of before #196, which had no passes. */
export function attemptHeading(attempt: Pick<RankedAttempt, 'pass' | 'rank'>): string {
  return attempt.pass === null
    ? `Essai ${attempt.rank}`
    : `Passe ${attempt.pass} · essai ${attempt.rank}`;
}

/**
 * « Entrée servie · passe 1 · essai 2 » (§10: each entry is marked with its pass,
 * one per call, the retry included), or « Entrée servie · essai 3 » for the one
 * entry of before #196. `index` is the entry's place in the list.
 */
export function promptHeading(prompts: readonly NoyauAnalystPrompt[], index: number): string {
  const prompt = prompts[index];
  if (prompt === undefined || prompt.pass === null) return `Entrée servie · essai ${index + 1}`;
  const rank = prompts.slice(0, index + 1).filter((entry) => entry.pass === prompt.pass).length;
  return `Entrée servie · passe ${prompt.pass} · essai ${rank}`;
}

/* ------------------------------------------------- why an attempt failed */

/**
 * What was wrong with an answer that CAME BACK and could not be used. The shape
 * checks are the pass's own: the first answers `objectives`, the second `styles`,
 * and a call of before #196 answered six `scores`.
 */
export type AnswerFault =
  | { readonly kind: 'cut' }
  | { readonly kind: 'not-json' }
  | { readonly kind: 'readable-now' }
  | { readonly kind: 'six-count'; readonly count: number }
  | { readonly kind: 'six-contract' }
  | { readonly kind: 'objectives-missing' }
  | { readonly kind: 'objectives-contract' }
  | { readonly kind: 'styles-missing' }
  | { readonly kind: 'styles-contract' };

/**
 * Why a call that came back did not give its pass's answer, or null on a call
 * that failed another way (`failedReason`) or answered.
 *
 * Two readings come BEFORE the parse, and both are the server's: a call the
 * provider stopped at `max_tokens` is a cut answer - its JSON ends mid-way, and
 * « pas du JSON » would send a reader to the prompt when the repair is the ceiling
 * (#96) -, and the ``` fence the server forgives is stripped by the same `unfenced`.
 * What is checked after the parse is the SHAPE the pass asks for, as far as the
 * screen can know it: the objectives the first pass was served are not on the
 * determination of a failure, so the exact objective that is missing is not named.
 */
export function answerFaultOf(call: NoyauAnalystCall): AnswerFault | null {
  if (call.failedReason !== null || !call.malformed) return null;
  if (call.stopReason === 'max_tokens') return { kind: 'cut' };
  let parsed: unknown;
  try {
    parsed = JSON.parse(unfenced(call.output));
  } catch {
    return { kind: 'not-json' };
  }
  if (call.pass === 1) {
    const list = (parsed as { objectives?: unknown } | null)?.objectives;
    return { kind: Array.isArray(list) ? 'objectives-contract' : 'objectives-missing' };
  }
  if (call.pass === 2) {
    const list = (parsed as { styles?: unknown } | null)?.styles;
    return { kind: Array.isArray(list) ? 'styles-contract' : 'styles-missing' };
  }
  if (noyauAnalystOutputSchema.safeParse(parsed).success) return { kind: 'readable-now' };
  const scores = (parsed as { scores?: unknown } | null)?.scores;
  if (Array.isArray(scores) && scores.length !== NOYAU_CODES.length) {
    return { kind: 'six-count', count: scores.length };
  }
  return { kind: 'six-contract' };
}

/** The fault as a sentence, in the words the cause of an attempt is told in. */
export function answerFaultSentence(fault: AnswerFault): string {
  switch (fault.kind) {
    case 'cut':
      return TRUNCATED_OUTPUT_SENTENCE;
    case 'not-json':
      return 'Sortie illisible : la réponse n’est pas du JSON.';
    case 'readable-now':
      return 'Sortie écartée à l’époque, lisible aujourd’hui.';
    case 'six-count':
      return `Sortie de mauvaise forme : ${fault.count} notes au lieu de six.`;
    case 'six-contract':
      return 'Sortie de mauvaise forme : le contrat des six notes n’est pas tenu.';
    case 'objectives-missing':
      return 'Sortie de mauvaise forme : la passe 1 n’a pas rendu la liste « objectives ».';
    case 'objectives-contract':
      return 'Sortie de mauvaise forme : un objectif manque, est en trop ou inconnu, ou ses trois parts ne font pas 100.';
    case 'styles-missing':
      return 'Sortie de mauvaise forme : la passe 2 n’a pas rendu la liste « styles ».';
    case 'styles-contract':
      return 'Sortie de mauvaise forme : un candidat manque, est en trop, ou une note sort de 0 à 100.';
  }
}

/**
 * The same cause in two or three words, for the yellow line of a style that could
 * not be read: « sortie illisible, puis délai dépassé ». A call that never came
 * back says what the provider said; one that did, what was wrong with it.
 */
export function attemptCauseWords(call: NoyauAnalystCall): string | null {
  if (call.failedReason !== null) return call.failedReason;
  const fault = answerFaultOf(call);
  if (fault === null) return null;
  switch (fault.kind) {
    case 'cut':
      return 'réponse coupée';
    case 'not-json':
      return 'sortie illisible';
    case 'readable-now':
      return 'sortie écartée à l’époque';
    default:
      return 'sortie de mauvaise forme';
  }
}

/**
 * « La passe 2 a échoué deux fois (sortie illisible, puis délai dépassé) : les notes
 * sont celles de la compatibilité seule. » - the yellow line of a style absent
 * (§7.5, the maquette `ParcoursEtats`). The causes are the second pass's attempts,
 * in order; with none to name, the brackets go.
 */
export function styleAbsentSentence(calls: readonly NoyauAnalystCall[]): string {
  const attempts = calls.filter((call) => call.pass === 2);
  const times =
    attempts.length === 0
      ? ''
      : attempts.length === 2
        ? ' deux fois'
        : attempts.length === 1
          ? ' une fois'
          : ` ${attempts.length} fois`;
  const causes = attempts.map((call) => attemptCauseWords(call)).filter((one) => one !== null);
  const bracket = causes.length === 0 ? '' : ` (${causes.join(', puis ')})`;
  return `La passe 2 a échoué${times}${bracket} : les notes sont celles de la compatibilité seule.`;
}

/* ------------------------------------------------------ why a session failed */

/**
 * What a determination settled `failed` WITHOUT a call says about the session that
 * froze no complete instrument (§9.2, G22): the reason, and what the frozen
 * protocol lacked - « n'a pas de niveaux », « n'a pas d'objectif au niveau Comment »,
 * « n'a pas de lecture pour la passe 2 ».
 *
 * A protocol with no level at all is the case of a session started before the
 * section existed, and it has no readings either, so the levels alone are named;
 * the readings are named when the levels are there.
 */
function lackOf(failure: AnalysisFailure): string | null {
  const lacks: string[] = [];
  if (failure.levelsMissing.length === ANALYST_LEVELS.length) {
    lacks.push('de niveaux');
  } else {
    if (failure.levelsMissing.length > 0) {
      lacks.push(`d’objectif ${analystLevelsPhrase(failure.levelsMissing)}`);
    }
    if (failure.readingsMissing.length > 0) {
      lacks.push(
        failure.readingsMissing.length === 2
          ? 'de lecture pour les deux passes'
          : `de lecture pour la passe ${failure.readingsMissing[0]}`,
      );
    }
  }
  return lacks.length === 0 ? null : `n’a pas ${lacks.join(' ni ')}`;
}

/**
 * The sentence of a determination that failed before any call, from its reason
 * (`analysis.failure`): the maquette `ParcoursEtats`, « Le protocole figé de cette
 * session n'a pas de niveaux : elle a démarré avant #196, la séquence du noyau est
 * sautée. ».
 */
export function failureSentence(failure: AnalysisFailure): string {
  const lack = lackOf(failure);
  if (failure.reason === 'method-missing') {
    const alone =
      'Cette session n’a figé aucune version de Méthode : elle a démarré avant #196, la séquence du noyau est sautée.';
    return lack === null ? alone : `${alone} Son protocole figé ${lack} non plus.`;
  }
  return `Le protocole figé de cette session ${lack ?? 'n’a pas de section Analyste complète'} : elle a démarré avant #196, la séquence du noyau est sautée.`;
}

/* ------------------------------------------------------------ the first pass */

/** The colour of each function - the tokens of `styles.css`, never a hex: the guard refuses one outside it. */
export const FUNCTION_BAR_CLASS: Readonly<Record<AnalystFunction, string>> = {
  S: 'bg-bleu-nuit',
  G: 'bg-rouge-deep',
  P: 'bg-vert-deep',
};

/** A part narrower than this draws no letter in its segment: « S 10 » fits in 10 % of the bar, « G 7 » does not. */
export const FUNCTION_BAR_LABEL_MIN = 10;

/** One segment of the bar of the three parts. */
export type FunctionBarSegment = {
  readonly fn: AnalystFunction;
  readonly share: number;
  /** « G 72 », or null when the segment is too narrow to carry it. */
  readonly label: string | null;
};

/** The bar of an objective: S, G and P in that order, each as wide as its part of 100. */
export function functionBar(shares: AnalysisShares): FunctionBarSegment[] {
  return ANALYST_FUNCTIONS.map((fn) => ({
    fn,
    share: shares[fn],
    label: shares[fn] >= FUNCTION_BAR_LABEL_MIN ? `${fn} ${shares[fn]}` : null,
  }));
}

/** « S 10 · G 72 · P 18 » - the same parts in figures, under the bar, always whole. */
export function sharesLine(shares: AnalysisShares): string {
  return ANALYST_FUNCTIONS.map((fn) => `${fn} ${shares[fn]}`).join(' · ');
}

/** « 6 objectifs lus, trois parts sur 100 chacun » - what the section says when it is folded. */
export function functionsSummary(analysis: NoyauAnalysis): string {
  const count = analysis.functions.length;
  return count > 1
    ? `${count} objectifs lus, trois parts sur 100 chacun`
    : `${count} objectif lu, trois parts sur 100`;
}

/* ------------------------------------------------------------------ the levels */

/** The tone of the pill of a level: a fixed one is a result, a dropped one a warning, the rest is neutral. */
function levelTone(state: AnalysisLevel['state']): BadgeTone {
  if (isLevelFixed(state)) return 'success';
  if (state === 'dropped') return 'warning';
  return 'neutral';
}

/** One row of the table of the levels. */
export type LevelRow = {
  readonly level: AnalystLevel;
  readonly levelLabel: string;
  /** The function the level is - or WAS, when a conflict set it aside - fixed on. */
  readonly fn: AnalystFunction | null;
  readonly fnName: string | null;
  readonly state: AnalysisLevel['state'];
  readonly stateLabel: string;
  readonly tone: BadgeTone;
  /** What the code read: « 72 et 84 », « symétrie 74 et 71 », « deux fonctions : G 48, P 62 ». */
  readonly parts: string;
};

/**
 * What a level that is not fixed says of the parts it read: each objective leans on
 * a function, and the level is not fixed because they lean on different ones -
 * « deux fonctions : G 48, P 62 » - or because they lean on one and not hard enough
 * - « S 74 et 65, sous le seuil de 70 ».
 */
function unfixedParts(analysis: NoyauAnalysis, level: AnalysisLevel): string {
  const leaning = new Map<AnalystFunction, number[]>();
  for (const row of analysis.functions) {
    if (row.level !== level.level) continue;
    const lead = ANALYST_FUNCTIONS.reduce((best, fn) =>
      row.shares[fn] > row.shares[best] ? fn : best,
    );
    leaning.set(lead, [...(leaning.get(lead) ?? []), row.shares[lead]]);
  }
  const entries = [...leaning];
  const [only] = entries;
  if (only === undefined) return 'aucune part lue';
  if (entries.length === 1) {
    const bound = level.threshold === null ? '' : `, sous le seuil de ${level.threshold}`;
    return `${only[0]} ${sharesPhrase(only[1])}${bound}`;
  }
  const words = entries.length === 2 ? 'deux' : 'trois';
  const figures = entries.map(([fn, shares]) => `${fn} ${Math.max(...shares)}`);
  return `${words} fonctions : ${figures.join(', ')}`;
}

/** The conflict that set a level aside, if any. */
function conflictOf(analysis: NoyauAnalysis, level: AnalystLevel) {
  return analysis.conflicts.find((conflict) => conflict.dropped.includes(level)) ?? null;
}

/**
 * The three levels as the table draws them, in the order of a triplet: the level,
 * the function it is fixed on, its state and what decided it, the parts read.
 *
 * A dropped level keeps the function it was fixed on and says WHY it was set aside
 * - « écarté : conflit avec Quoi, plus sûr » - and how it had been fixed:
 * « symétrie 74 et 71 ».
 */
export function levelRows(analysis: NoyauAnalysis): LevelRow[] {
  return analysis.levels.map((level) => {
    const decisive = level.decisive.map((part) => part.share);
    const conflict = level.state === 'dropped' ? conflictOf(analysis, level.level) : null;
    let parts: string;
    switch (level.state) {
      case 'fixed-pair':
      case 'fixed-solo':
        parts = sharesPhrase(decisive);
        break;
      case 'dropped':
        parts = `${decisive.length > 1 ? 'symétrie' : 'objectif seul'} ${sharesPhrase(decisive)}`;
        break;
      case 'nothing-to-read':
        parts = 'aucun objectif lisible';
        break;
      default:
        parts = unfixedParts(analysis, level);
    }
    return {
      level: level.level,
      levelLabel: ANALYST_LEVEL_LABELS[level.level],
      fn: level.function,
      fnName: level.function === null ? null : ANALYST_FUNCTION_NAMES[level.function],
      state: level.state,
      stateLabel:
        conflict === null
          ? ANALYSIS_LEVEL_STATE_LABELS[level.state]
          : `écarté : conflit avec ${ANALYST_LEVEL_LABELS[conflict.kept]}, plus sûr`,
      tone: levelTone(level.state),
      parts,
    };
  });
}

/** « Seuil d'une paire 70 · d'un objectif seul 85 » - the two thresholds that applied, as parts. */
export function thresholdsLine(analysis: NoyauAnalysis): TextPart[] {
  const { pairThreshold, soloThreshold } = analysis.settings;
  return [
    plain('Seuil d’une paire '),
    bold(String(pairThreshold)),
    plain(' · d’un objectif seul '),
    bold(String(soloThreshold)),
  ];
}

/**
 * One sentence per conflict (§6.3, G9): which function two levels were fixed on,
 * which level the code kept, and which of K4's rules decided it - « Conflit sur le
 * Souverain : le Quoi est gardé (la plus haute des plus petites parts), le Pourquoi
 * est écarté. ».
 */
export function conflictSentences(analysis: NoyauAnalysis): string[] {
  return analysis.conflicts.map((conflict) => {
    const dropped = conflict.dropped.map((level) => `le ${ANALYST_LEVEL_LABELS[level]}`);
    const verb = dropped.length > 1 ? 'sont écartés' : 'est écarté';
    const names =
      dropped.length > 1
        ? `${dropped.slice(0, -1).join(', ')} et ${dropped.at(-1)}`
        : (dropped[0] ?? '');
    return `Conflit sur le ${ANALYST_FUNCTION_NAMES[conflict.function]} : le ${ANALYST_LEVEL_LABELS[conflict.kept]} est gardé (${ANALYSIS_CONFLICT_DECIDER_LABELS[conflict.decidedBy]}), ${names} ${verb}.`;
  });
}

/** The levels folded into one line: « Pourquoi G et Quoi S fixés en symétrie, Comment non fixé ». */
export function levelsSummary(analysis: NoyauAnalysis): TextPart[] {
  const named = (state: AnalysisLevel['state']) =>
    analysis.levels.filter((level) => level.state === state);
  const names = (levels: readonly AnalysisLevel[]) =>
    joinedWithAnd(levels.map((level) => [plain(ANALYST_LEVEL_LABELS[level.level])]));
  const withFunction = (levels: readonly AnalysisLevel[]) =>
    joinedWithAnd(
      levels.map((level) => [
        plain(`${ANALYST_LEVEL_LABELS[level.level]} `),
        bold(level.function ?? ''),
      ]),
    );

  const groups: TextPart[][] = [];
  const pairs = named('fixed-pair');
  if (pairs.length > 0) {
    groups.push([
      ...withFunction(pairs),
      plain(pairs.length > 1 ? ' fixés en symétrie' : ' fixé en symétrie'),
    ]);
  }
  const solos = named('fixed-solo');
  if (solos.length > 0) {
    groups.push([
      ...withFunction(solos),
      plain(solos.length > 1 ? ' fixés, objectif seul' : ' fixé, objectif seul'),
    ]);
  }
  const dropped = named('dropped');
  if (dropped.length > 0) {
    groups.push([...names(dropped), plain(' écarté (conflit)')]);
  }
  const unfixed = named('not-fixed');
  if (unfixed.length > 0) {
    groups.push([...names(unfixed), plain(unfixed.length > 1 ? ' non fixés' : ' non fixé')]);
  }
  const empty = named('nothing-to-read');
  if (empty.length > 0) groups.push([...names(empty), plain(' : rien à lire')]);

  const parts: TextPart[] = [];
  groups.forEach((group, index) => {
    if (index > 0) parts.push(plain(', '));
    parts.push(...group);
  });
  return parts;
}

/* --------------------------------------------------------------- elimination */

/** « Guerrier Humain (GPS) » - the deposited name, then the triplet. */
export function noyauNamed(code: NoyauCode): string {
  return `${noyauOf(code).name} (${code})`;
}

/** The candidates the levels left and the noyaux they eliminated, each with the sentence of its note. */
export type EliminationView = {
  readonly candidates: readonly NoyauCode[];
  readonly eliminated: readonly { readonly noyau: NoyauCode; readonly sentence: string }[];
};

/**
 * The candidates, then the eliminated (§6.4). The sentence of an eliminated noyau
 * is the one the code wrote on its note (K9, `eliminatedSentence`): it names the
 * FIRST fixed level the noyau contradicts - « Éliminé : le Pourquoi est fixé
 * Guerrier. » - and is rebuilt here from the levels, the very function the engine
 * used.
 */
export function eliminationOf(analysis: NoyauAnalysis): EliminationView {
  const candidates = analysis.noyaux.filter((row) => row.candidate).map((row) => row.noyau);
  const eliminated = analysis.noyaux
    .filter((row) => !row.candidate)
    .map((row) => {
      const level = analysis.levels.find((one) => one.level === row.eliminatedBy);
      const sentence =
        row.eliminatedBy === null || level === undefined || level.function === null
          ? 'Éliminé.'
          : eliminatedSentence(row.eliminatedBy, level.function);
      return { noyau: row.noyau, sentence };
    });
  return { candidates, eliminated };
}

/**
 * « Reste 1 candidat : Guerrier Humain (GPS), 5 éliminés » - the section folded.
 * The names are said up to two candidates; past that the count says it.
 */
export function eliminationSummary(view: EliminationView): TextPart[] {
  const count = view.candidates.length;
  const parts: TextPart[] = [
    plain(
      `${count === 1 ? 'Reste' : 'Restent'} ${count} ${count === 1 ? 'candidat' : 'candidats'}`,
    ),
  ];
  if (count <= 2) {
    parts.push(plain(' : '));
    parts.push(...joinedWithAnd(view.candidates.map((code) => [bold(noyauNamed(code))])));
  }
  const gone = view.eliminated.length;
  parts.push(
    plain(gone === 0 ? ', aucun éliminé' : `, ${gone} ${gone > 1 ? 'éliminés' : 'éliminé'}`),
  );
  return parts;
}

/* ------------------------------------------------------------ the second pass */

/** « Soutiennent » and « Contredisent » have a colour each, the tokens' deep ink. */
export const PASSAGE_SIDE_CLASS = {
  supporting: 'text-vert-deep',
  contradicting: 'text-rouge-deep',
} as const;

/** « 1 mot introuvable dans les réponses, écarté » - the planche's words for the passages the check set aside (§7.4), or null when none was. */
export function discardedSentence(count: number): string | null {
  if (count <= 0) return null;
  return count === 1
    ? '1 mot introuvable dans les réponses, écarté'
    : `${count} mots introuvables dans les réponses, écartés`;
}

/** The second pass folded into one line: « 1 candidat noté : GPS 74 ». */
export function styleSummary(analysis: NoyauAnalysis): string {
  const notes = analysis.style?.state === 'rendered' ? analysis.style.notes : [];
  const head = `${notes.length} ${notes.length > 1 ? 'candidats notés' : 'candidat noté'}`;
  if (notes.length === 0 || notes.length > 2) return head;
  return `${head} : ${notes.map((note) => `${note.noyau} ${note.score}`).join(' · ')}`;
}

/* ------------------------------------------------------------------ six notes */

/** One row of the table of the six notes. */
export type NoteRow = {
  readonly noyau: NoyauCode;
  readonly score: number;
  /** Null only on an analysis that does not carry this noyau, which the engine never writes. */
  readonly compatibility: number | null;
  /** The style the second pass noted: null for an eliminated noyau, and for a candidate whose style is absent. */
  readonly style: number | null;
  readonly place: 'principal' | 'second' | null;
  readonly eliminated: boolean;
};

/**
 * The six notes, in the order every reading of them uses - the candidates, then the
 * eliminated, the best first - each with its compatibility and its style (§8).
 */
export function noteRows(determination: NoyauDetermination, analysis: NoyauAnalysis): NoteRow[] {
  const gone = eliminatedNoyaux(analysis);
  return sortedNoyauScores({ ...determination, eliminated: gone }).map((score) => {
    const row = analysis.noyaux.find((one) => one.noyau === score.noyau);
    return {
      noyau: score.noyau,
      score: score.score,
      compatibility: row?.compatibility ?? null,
      style: row?.style ?? null,
      place:
        score.noyau === determination.principal
          ? 'principal'
          : score.noyau === determination.second
            ? 'second'
            : null,
      eliminated: gone.includes(score.noyau),
    };
  });
}

/** « Note d'un candidat : 50 % de compatibilité, 50 % de style. Un éliminé reste sous le dernier candidat. » */
export function weightSentence(styleWeight: number): string {
  return `Note d’un candidat : ${percent(100 - styleWeight)} de compatibilité, ${percent(styleWeight)} de style. Un éliminé reste sous le dernier candidat.`;
}

/** The six notes folded into one line: « Principal GPS 72, second PGS 42 ». */
export function notesSummary(rows: readonly NoteRow[]): string {
  const place = (which: 'principal' | 'second', word: string) => {
    const row = rows.find((one) => one.place === which);
    return row === undefined ? null : `${word} ${row.noyau} ${row.score}`;
  };
  return [place('principal', 'Principal'), place('second', 'second')]
    .filter((part) => part !== null)
    .join(', ');
}

/* ----------------------------------------------------------- the instrument */

/**
 * The line of the instrument (§11.1): which protocol and which version of
 * Méthode the analyst was read with, the version of the assembly, and the
 * fingerprint of the instrument - the two systems together (K11).
 *
 * Every field can be absent and says so by being null: no protocol when the
 * frozen one does not read back, no Méthode on a session that froze none, no
 * fingerprint on a header written before the first pass.
 */
export type InstrumentFacts = {
  readonly protocol: {
    readonly protocolId: string;
    readonly versionId: string;
    readonly name: string;
    readonly versionNumber: number;
  } | null;
  readonly method: { readonly versionId: string; readonly number: number } | null;
  readonly promptVersion: string | null;
  readonly fingerprint: string | null;
};

/**
 * The facts of a determination of the analyst in two passes - or null when no call
 * was made: a line naming an instrument no call received would describe an event
 * that did not happen. The protocol is the one the SESSION froze, never today's.
 *
 * Built from the analysis and the frozen protocol, NOT from `analystReadingFacts`:
 * that helper reads the reading of before #196 and says « générique » of every
 * protocol that has none - which is every protocol born since the section exists.
 */
export function instrumentFacts(
  analysis: NoyauAnalysis,
  protocol: PlayedProtocol | null,
  calls: readonly NoyauAnalystCall[],
): InstrumentFacts | null {
  const last = calls.at(-1);
  if (last === undefined) return null;
  return {
    protocol:
      protocol === null
        ? null
        : {
            protocolId: protocol.id,
            versionId: protocol.versionId,
            name: protocol.name,
            versionNumber: protocol.versionNumber,
          },
    method: analysis.settings.method,
    promptVersion: last.promptVersion,
    fingerprint: analysis.instrument,
  };
}

/** The same for a TRIAL: the development version it was read with, and its Méthode. Null until a call came back. */
export function trialInstrumentFacts(
  trial: NoyauTrial,
  analysis: NoyauAnalysis,
): InstrumentFacts | null {
  const last = trial.determination.calls.at(-1);
  if (last === undefined) return null;
  return {
    protocol: {
      protocolId: trial.reading.protocolId,
      versionId: trial.reading.versionId,
      name: trial.reading.name,
      versionNumber: trial.reading.versionNumber,
    },
    method: analysis.settings.method,
    promptVersion: last.promptVersion,
    fingerprint: analysis.instrument,
  };
}
